import { createContext, useRef, useEffect, useCallback, useMemo } from "react";
// import { min, parseISO, isEqual } from "date-fns";
import { useLocation } from "react-router-dom";
// firestore
import {
  doc,
  limit,
  query,
  where,
  getDocs,
  orderBy,
  deleteDoc,
  startAfter,
  collection,
  onSnapshot,
  setDoc,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../services/firebase";
// utils
import axios from "../utils/axios";
// products
import actionTypes from "../common/actionTypes";
import { orderReducer } from "../reducers";
// context
import useAuthContext from "../hooks/useAuthContext";
// constants
import {
  APP_ROUTES,
  DATABASE_STATUTES,
  ORDERS_CHUNK_LIMIT,
  PREORDER_NOTIFICATIONS_CHUNK_LIMIT,
} from "../common/constants";
import { LOG_TYPES } from "./ActivityLogsContext";
// hooks
import useCustomReducer from "../hooks/useCustomReducer";
import useActivityLogsContext from "../hooks/useActivityLogsContext";
// utils
import shopifyAdminFetch from "../utils/shopifyAdminFetch";

// order constants
const SHOPS = "Shops";
const ORDERS = "PreOrders";
const ORDERS_PARTIAL = "Orders";
const ORDERS_NOTIFICATIONS = "PreOrderNotifications";
const ORDERS_COLLECT_PAYMENTS_URL = "shopify/payments";
const EXPORT_ORDERS_NOTIFICATIONS_CSV_URL = "shop/po/export";
const NOTIFICATIONS = "Notifications";

const getProductImages = (id) => `
{
  product(id: "gid://shopify/Product/${id}") {
    images(first:1) {
      edges {
        cursor
        node {
          originalSrc
        }
      }
    }
  }
}
`;

const fetchOrderData = (id) => `
{
  order(id: "gid://shopify/Order/${id}") {
    displayFulfillmentStatus
    lineItems(first:100) {
      edges {
        node {
          sellingPlan {
            name
            sellingPlanId
          }
          id
        }
      }
    }
    paymentTerms {
      id
      paymentSchedules(first:1) {
        edges {
          node {
            id
            dueAt
            amount {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
}
`;

// TODO Release require read_merchant_managed_fulfillment_orders permission
// fulfillmentOrders(first:100) {
//   edges {
//     node {
//       id
//       status
//       fulfillAt
//       lineItems(first:1) {
//         edges {
//           node {
//             variantTitle
//             productTitle
//           }
//         }
//       }
//     }
//   }
// }
const fetchOrderFulfillmentStatus = (id) => `
{
  order(id: "gid://shopify/Order/${id}") {
    displayFulfillmentStatus
  }
}
`;

const initialState = {
  orders: {
    data: [],
    filtered: [],
  },
  notifications: {
    data: [],
    filtered: [],
  },
  statuses: {
    loadOrders: DATABASE_STATUTES.NOT_LOADED,
    loadOrderNotifications: DATABASE_STATUTES.NOT_LOADED,
    loadOrderEmails: DATABASE_STATUTES.NOT_LOADED,
  },
  orderEmails: {
    data: [],
    filtered: [],
  },
  error: null,
  productImgs: {},
  processingPayments: false,
  pagination: {
    allOrdersCount: 0,
    allOrderNotifications: 0,
  },
};

const OrdersContext = createContext({
  ...initialState,
  fetchOrders: () => {},
  fetchProductImages: () => Promise.resolve(),
  deleteOrder: () => {},
  getOrderByNumber: () => Promise.resolve(),
  collectPayments: () => Promise.resolve(),
  processOrderPayment: () => Promise.resolve(),
  exportOrdersNotificationsCSV: () => Promise.resolve(),
  searchOrderByNumber: () => Promise.resolve(),
  searchOrderNotificationsByOrderName: () => Promise.resolve(),
  getPreOrderEmailsHistory: () => Promise.resolve(),
});

const OrdersProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(orderReducer, initialState);

  // orders
  const ordersRef = useRef(null);
  const lastVisible = useRef(null);
  // pre-order notifications
  const ordersNotificationsRef = useRef(null);
  const lastVisibleOrdersNotification = useRef(null);
  const listeners = useRef([]);
  const orderEmails = useRef(null);

  const { user } = useAuthContext();
  const { writeLog } = useActivityLogsContext();

  const { pathname } = useLocation();
  const isOrdersPage = pathname.startsWith(APP_ROUTES.ORDERS.ROOT);
  const isOrderNotificationsPage = pathname.startsWith(APP_ROUTES.ORDERS.NOTIFICATIONS);

  useEffect(() => {
    if (user?.domain && isOrdersPage) {
      dispatch({
        type: actionTypes.FETCH_ORDERS,
      });

      ordersRef.current = collection(db, SHOPS, user.domain, ORDERS);
      orderEmails.current = collection(db, NOTIFICATIONS);

      getCountFromServer(ordersRef.current)
        .then((snapshot) => {
          const totalOrders = snapshot.data().count;

          dispatch({
            type: actionTypes.FIRESTORE_SET_TOTAL_ORDERS_COUNT,
            payload: totalOrders,
          });
        })
        .catch((error) => {
          console.error("Error getting order count:", error);
        });

      try {
        const listener = onSnapshot(
          query(ordersRef.current, orderBy("createdAt", "desc"), limit(ORDERS_CHUNK_LIMIT)),
          async (documentSnapshots) => {
            const orders = await Promise.all(
              documentSnapshots.docs.map(async (doc) => {
                const {
                  data: { order },
                } = await shopifyAdminFetch({ query: fetchOrderFulfillmentStatus(doc.id) });

                return {
                  ...doc.data(),
                  id: doc.id,
                  fulfillmentStatus: order?.displayFulfillmentStatus || "",
                };
              })
            );

            dispatch({
              type: actionTypes.FETCH_ORDERS_SUCCESS,
              payload: {
                orders,
                startIndex: 0,
                endIndex: orders.length,
                append: false,
              },
            });

            lastVisible.current = documentSnapshots.docs.at(-1);
          }
        );

        listeners.current.push(listener);
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_ORDERS_FAILURE,
          payload: error,
        });
      }
    }
  }, [dispatch, user?.domain, isOrdersPage]);

  const searchOrderByNumber = useCallback(
    async (name) => {
      try {
        dispatch({
          type: actionTypes.SEARCH_ORDERS,
        });

        if (!name) {
          dispatch({
            type: actionTypes.SEARCH_ORDERS_SUCCESS,
            payload: [],
          });

          return;
        }

        const documentSnapshots = await getDocs(
          query(ordersRef.current, where("name", ">=", name), where("name", "<=", `${name}~`))
        );

        const filteredOrders = await Promise.all(
          documentSnapshots.docs.map(async (doc) => {
            const {
              data: { order },
            } = await shopifyAdminFetch({ query: fetchOrderFulfillmentStatus(doc.id) });

            return {
              ...doc.data(),
              id: doc.id,
              fulfillmentStatus: order?.displayFulfillmentStatus || "",
            };
          })
        );

        dispatch({
          type: actionTypes.SEARCH_ORDERS_SUCCESS,
          payload: filteredOrders,
        });
      } catch (error) {
        dispatch({
          type: actionTypes.SEARCH_ORDERS_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch]
  );

  const subscribeToNextChunkOfOrders = useCallback(async () => {
    try {
      dispatch({
        type: actionTypes.FETCH_ORDERS,
      });

      const listener = onSnapshot(
        query(
          ordersRef.current,
          orderBy("createdAt", "desc"),
          startAfter(lastVisible.current),
          limit(ORDERS_CHUNK_LIMIT)
        ),
        async (documentSnapshots) => {
          const orders = await Promise.all(
            documentSnapshots.docs.map(async (doc) => {
              const {
                data: { order },
              } = await shopifyAdminFetch({ query: fetchOrderFulfillmentStatus(doc.id) });

              return {
                ...doc.data(),
                id: doc.id,
                fulfillmentStatus: order?.displayFulfillmentStatus || "",
              };
            })
          );

          dispatch({
            type: actionTypes.FETCH_MORE_ORDERS_SUCCESS,
            payload: {
              orders,
              startIndex: state.orders.data.length,
              endIndex: state.orders.data.length + orders.length,
            },
          });

          lastVisible.current = documentSnapshots.docs.at(-1);
        }
      );

      listeners.current.push(listener);
    } catch (error) {
      dispatch({
        type: actionTypes.FETCH_ORDERS_FAILURE,
        payload: error,
      });
    }
  }, [dispatch, state.orders.data.length]);

  // Clear all listeners
  useEffect(
    () => () => {
      listeners.current.forEach((listener) => listener());
      listeners.current = [];
    },
    [isOrdersPage]
  );

  const fetchPreOrderNotifications = useCallback(
    async ({ nextChunk = false, sortBy = "orderName" } = {}) => {
      dispatch({
        type: actionTypes.FETCH_ORDERS_NOTIFICATIONS,
      });

      getCountFromServer(ordersNotificationsRef.current)
        .then((snapshot) => {
          const totalOrdersNotifications = snapshot.data().count;

          dispatch({
            type: actionTypes.FIRESTORE_SET_TOTAL_ORDERS_NOTIFICATIONS_COUNT,
            payload: totalOrdersNotifications,
          });
        })
        .catch((error) => {
          console.error("Error getting order count:", error);
        });

      try {
        const notifications = [];

        const q = nextChunk
          ? query(
              ordersNotificationsRef.current,
              orderBy(sortBy, "desc"),
              startAfter(lastVisibleOrdersNotification.current),
              limit(PREORDER_NOTIFICATIONS_CHUNK_LIMIT)
            )
          : query(ordersNotificationsRef.current, orderBy(sortBy, "desc"), limit(PREORDER_NOTIFICATIONS_CHUNK_LIMIT));

        const documentSnapshots = await getDocs(q);

        documentSnapshots.forEach((doc) => {
          notifications.push({
            ...doc.data(),
            id: doc.id,
          });
        });

        lastVisibleOrdersNotification.current = documentSnapshots.docs.at(-1);

        dispatch({
          type: actionTypes.FETCH_ORDERS_NOTIFICATIONS_SUCCESS,
          payload: {
            notifications,
          },
        });
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_ORDERS_NOTIFICATIONS_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch]
  );

  const searchOrderNotificationsByOrderName = useCallback(
    async (orderName) => {
      try {
        dispatch({
          type: actionTypes.SEARCH_ORDER_NOTIFICATIONS,
        });

        if (!orderName) {
          dispatch({
            type: actionTypes.SEARCH_ORDER_NOTIFICATIONS_SUCCESS,
            payload: [],
          });

          return;
        }

        const filteredOrderNotifications = [];

        const documentSnapshots = await getDocs(
          query(
            ordersNotificationsRef.current,
            where("orderName", ">=", orderName),
            where("orderName", "<=", `${orderName}~`)
          )
        );

        documentSnapshots.docs.forEach((doc) => {
          filteredOrderNotifications.push({
            ...doc.data(),
            id: doc.id,
          });
        });

        dispatch({
          type: actionTypes.SEARCH_ORDER_NOTIFICATIONS_SUCCESS,
          payload: filteredOrderNotifications,
        });
      } catch (error) {
        dispatch({
          type: actionTypes.SEARCH_ORDER_NOTIFICATIONS_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch]
  );

  // Get first chunk of orders notifications
  useEffect(() => {
    if (user?.domain && isOrderNotificationsPage) {
      ordersNotificationsRef.current = collection(db, SHOPS, user.domain, ORDERS_NOTIFICATIONS);

      fetchPreOrderNotifications();
    }
  }, [fetchPreOrderNotifications, user?.domain, isOrderNotificationsPage]);

  const fetchNextChunkOfPreOrderNotifications = useCallback(
    (sortBy) => {
      fetchPreOrderNotifications({ nextChunk: true, sortBy });
    },
    [fetchPreOrderNotifications]
  );

  const fetchProductImages = useCallback(
    async (productId) => {
      if (state.productImgs[productId]) {
        return state.productImgs[productId];
      }

      // Product is not in cache, so we request the image and update the cache.
      const {
        data: { product },
      } = await shopifyAdminFetch({ query: getProductImages(productId) });

      const imgSrc = product?.images?.edges[0]?.node.originalSrc;

      imgSrc &&
        dispatch({
          type: actionTypes.SAVE_PRODUCT_IMGS,
          payload: {
            productId,
            imgSrc,
          },
        });

      return imgSrc;
    },
    [dispatch, state.productImgs]
  );

  const deleteOrder = useCallback(
    async (id) => {
      try {
        dispatch({
          type: actionTypes.DELETE_ORDER,
        });

        await deleteDoc(doc(db, SHOPS, user.domain, ORDERS, id));

        const order = state.orders.data.find((order) => order.id === id);

        if (order?.name) {
          await writeLog(LOG_TYPES.DELETE_ORDER, order.name);
        }

        dispatch({
          type: actionTypes.DELETE_ORDER_SUCCESS,
          payload: id,
        });
      } catch (error) {
        dispatch({
          type: actionTypes.DELETE_ORDER_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch, state.orders, user?.domain, writeLog]
  );

  const getOrderByNumber = useCallback(
    async (number) => {
      try {
        const order = [...state.orders.data, ...state.orders.filtered].find(
          ({ orderNumber }) => String(orderNumber) === number
        );

        if (order) {
          const { data } = await shopifyAdminFetch({ query: fetchOrderData(order.id) });
          // TODO Release require read_merchant_managed_fulfillment_orders permission
          // let earliestFulfillmentDate = min(
          //   fulfillmentOrders.edges
          //     .map(({ node }) =>
          //       node.fulfillAt ? parseISO(node.fulfillAt) : node.fulfillAt
          //     )
          //     .filter((date) => date)
          // );

          return {
            ...order,
            fulfillmentStatus: data.order?.displayFulfillmentStatus || "",
            ...(data.order?.paymentTerms?.id && {
              paymentTerms: {
                id: data.order.paymentTerms.id.split("/").pop(),
                payment_schedules: data.order.paymentTerms?.paymentSchedules.edges?.map(({ node }) => ({
                  ...node,
                  ...node.amount,
                })),
              },
            }),
            lineItems: await Promise.all(
              order.lineItems.map(async (lineItem) => {
                const lineItemWithSellingPlan = data.order?.lineItems.edges.find(({ node }) =>
                  node.id.includes(lineItem.id)
                );

                const originalSrc = await fetchProductImages(lineItem.productId);
                // TODO Release require read_merchant_managed_fulfillment_orders permission
                // const {
                //   node: { fulfillAt, status, id },
                // } = fulfillmentOrders.edges.find(({ node }) =>
                //   node.lineItems.edges.some(
                //     ({ node }) =>
                //       node.productTitle === lineItem.title &&
                //       node.variantTitle === lineItem.variantTitle
                //   )
                // );

                if (lineItemWithSellingPlan) {
                  return {
                    ...lineItem,
                    originalSrc,
                    sellingPlanName: lineItemWithSellingPlan.node?.sellingPlan?.name,
                    // fulfillmentId: id,
                    // fulfillAt,
                    // fulfillmentStatus: status,
                    // isNextFulfillment: fulfillAt
                    //   ? isEqual(parseISO(fulfillAt), earliestFulfillmentDate)
                    //   : false,
                  };
                }

                return {
                  ...lineItem,
                  originalSrc: originalSrc || "",
                };
              })
            ),
            // .sort(
            //   (lineItemA, lineItemB) =>
            //     lineItemB.isNextFulfillment - lineItemA.isNextFulfillment
            // ),
          };
        }

        return null;
      } catch (error) {
        console.log("%c [Error]:", "color: red; font-weight: bold;", error);
      }
    },
    [fetchProductImages, state.orders]
  );

  const collectPayments = async (ids) => {
    try {
      dispatch({
        type: actionTypes.PROCESSING_PAYMENTS,
      });

      await axios.post(ORDERS_COLLECT_PAYMENTS_URL, {
        ordersIds: ids,
      });

      dispatch({
        type: actionTypes.PROCESSING_PAYMENTS_SUCCESS,
      });

      return Promise.resolve();
    } catch (error) {
      dispatch({
        type: actionTypes.PROCESSING_PAYMENTS_FAILURE,
        payload: error,
      });

      return Promise.reject(error);
    }
  };

  // This function will mark the order as processed in our DB without triggering the payment.
  const processOrderPayment = async (id) => {
    const q = query(collection(db, ORDERS_PARTIAL), where("id", "==", String(id)));

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return;
    }

    querySnapshot.forEach(async (docOrder) => {
      const docId = docOrder.id;
      await setDoc(
        doc(db, ORDERS_PARTIAL, docId),
        {
          paymentProcessed: true,
        },
        { merge: true }
      );
    });
  };

  const exportOrdersNotificationsCSV = async (params) => {
    try {
      dispatch({ type: actionTypes.EXPORT_ORDERS_NOTIFICATIONS_CSV });

      const {
        data: { size },
      } = await axios.post(EXPORT_ORDERS_NOTIFICATIONS_CSV_URL, params);

      dispatch({ type: actionTypes.EXPORT_ORDERS_NOTIFICATIONS_CSV_SUCCESS });

      return Promise.resolve(size);
    } catch (error) {
      dispatch({
        type: actionTypes.EXPORT_ORDERS_NOTIFICATIONS_CSV_FAILURE,
        payload: error,
      });

      return Promise.reject(error);
    }
  };

  const getPreOrderEmailsHistory = async (orderId) => {
    dispatch({
      type: actionTypes.FIRESTORE_ORDER_EMAILS,
    });

    try {
      const emailsHistory = [];

      if (orderId && orderEmails.current) {
        const q = query(orderEmails.current, where("orderId", "==", orderId), orderBy("date", "desc"));

        const documentSnapshots = await getDocs(q);

        documentSnapshots.forEach((doc) => {
          emailsHistory.push({
            ...doc.data(),
            id: doc.id,
          });
        });
        dispatch({
          type: actionTypes.FIRESTORE_ORDER_EMAILS_SUCCESS,
          payload: {
            emailsHistory,
          },
        });
        return emailsHistory;
      }
    } catch (error) {
      dispatch({
        type: actionTypes.FIRESTORE_ORDERS_NOTIFICATIONS_FAILURE,
        payload: error,
      });
      console.log("error getPreOrderEmailsHistory", error);
    }
  };

  const memoizedState = useMemo(
    () => ({
      ...state,
      fetchOrders: subscribeToNextChunkOfOrders,
      fetchPreOrderNotifications: fetchNextChunkOfPreOrderNotifications,
      fetchProductImages,
      deleteOrder,
      getOrderByNumber,
      collectPayments,
      processOrderPayment,
      exportOrdersNotificationsCSV,
      searchOrderByNumber,
      searchOrderNotificationsByOrderName,
      getPreOrderEmailsHistory,
    }),
    [
      state,
      deleteOrder,
      fetchProductImages,
      getOrderByNumber,
      subscribeToNextChunkOfOrders,
      fetchNextChunkOfPreOrderNotifications,
      searchOrderByNumber,
      searchOrderNotificationsByOrderName,
      getPreOrderEmailsHistory,
      // processOrderPayment
      // collectPayments
      // exportOrdersNotificationsCSV
    ]
  );

  return <OrdersContext.Provider value={memoizedState}>{children}</OrdersContext.Provider>;
};

export { OrdersContext, OrdersProvider };
